import { axios, APIBASE_REVIVERAPPLICATION } from "./ApiConfig";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import {
  AvailableDrivers,
  Collection,
  CollectionList,
  CollectionStatus,
  CreateTransportDevice,
  FullContainerQueue,
  TransportDevice,
  TransportDeviceType,
} from "@/types";

const getFullContainers = async () => axios.get(`${APIBASE_REVIVERAPPLICATION}api/collections/full-containers`);

export const useGetFullContainers = (options?: Partial<UseQueryOptions<FullContainerQueue>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<FullContainerQueue>({
    queryKey: ["getFullContainers"],
    queryFn: async () => {
      const result = await getFullContainers();
      return result.data;
    },
    ...queryOptions,
  });
};

const getDrivers = async (date: string) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/collections/drivers`, {
    params: { date },
  });

export const useGetDrivers = (date: string, options?: Partial<UseQueryOptions<AvailableDrivers>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<AvailableDrivers>({
    queryKey: ["getDrivers", date],
    queryFn: async () => {
      const result = await getDrivers(date);
      return result.data;
    },
    ...queryOptions,
  });
};

export const putReassignDriver = async (id: number, driverUserId: number) => {
  return axios.put<Collection>(`${APIBASE_REVIVERAPPLICATION}api/collections/${id}/assign-driver`, {
    driverUserId,
  });
};

export const putUpdatePickupDate = async (id: number, plannedDate: string) => {
  return axios.put<Collection>(`${APIBASE_REVIVERAPPLICATION}api/collections/${id}/planned-date`, {
    plannedDate,
  });
};

const getCollections = async (from: string, to: string, status?: CollectionStatus, sort?: string) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/collections`, {
    params: { from, to, status, sort },
  });

export const useGetCollections = (
  from: string,
  to: string,
  status?: CollectionStatus,
  sort?: string,
  options?: Partial<UseQueryOptions<CollectionList>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<CollectionList>({
    queryKey: ["getCollections", from, to, status, sort],
    queryFn: async () => {
      const result = await getCollections(from, to, status, sort);
      return result.data;
    },
    ...queryOptions,
  });
};

export const postScheduleCollection = async (containerId: number, plannedDate: string, driverUserId: number) => {
  return axios.post<Collection>(`${APIBASE_REVIVERAPPLICATION}api/collections`, {
    containerId,
    plannedDate,
    driverUserId,
  });
};

const getTransportDeviceTypes = async () => axios.get(`${APIBASE_REVIVERAPPLICATION}api/transport-devices/types`);

export const useGetTransportDeviceTypes = (options?: Partial<UseQueryOptions<TransportDeviceType[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<TransportDeviceType[]>({
    queryKey: ["getTransportDeviceTypes"],
    queryFn: async () => {
      const result = await getTransportDeviceTypes();
      return result.data;
    },
    ...queryOptions,
  });
};

const getTransportDevices = async () => axios.get(`${APIBASE_REVIVERAPPLICATION}api/transport-devices`);

export const useGetTransportDevices = (options?: Partial<UseQueryOptions<TransportDevice[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<TransportDevice[]>({
    queryKey: ["getTransportDevices"],
    queryFn: async () => {
      const result = await getTransportDevices();
      return result.data;
    },
    ...queryOptions,
  });
};

const getTransportDevice = async (id: number) => axios.get(`${APIBASE_REVIVERAPPLICATION}api/transport-devices/${id}`);

export const useGetTransportDevice = (id: number, options?: Partial<UseQueryOptions<TransportDevice>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<TransportDevice>({
    queryKey: ["getTransportDevice", id],
    queryFn: async () => {
      const result = await getTransportDevice(id);
      return result.data;
    },
    ...queryOptions,
  });
};

export const postCreateTransportDevice = async (data: CreateTransportDevice) => {
  return axios.post<TransportDevice>(`${APIBASE_REVIVERAPPLICATION}api/transport-devices`, data);
};

export const deleteTransportDevice = async (id: number) => {
  return axios.delete(`${APIBASE_REVIVERAPPLICATION}api/transport-devices/${id}`);
};
